import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TableActiveContext } from 'contexts/TableActiveContext';
import { TableIdContext } from 'contexts/TableIdContext';
import { useTableArrivalSnap, useTableSnap } from 'hooks/useTableSnap';
import type { Howl } from 'howler';
import { claimCue } from 'utils/attentionCues';
import { recordSfx, shortSrc } from 'utils/sfxTrace';
import { acquireSound, soundCacheKey } from 'utils/soundCache';
import { areTableSoundsEnabled } from 'utils/soundScope';

type PlayOptions = {
  /** Sprite id to play (see the `sprite` option). */
  id?: string;
  /** Play even when this hook was created with `soundEnabled: false`. */
  forceSoundEnabled?: boolean;
  /** One-shot rate override. */
  playbackRate?: number;
  /**
   * Per-play dedupe key, for cues whose identity changes each  call — the countdown
   * is `warn:5`, `warn:4`… Overrides the hook-level `dedupeKey`.
   */
  dedupeKey?: string;
};

type SoundOptions = {
  volume?: number;
  playbackRate?: number;
  soundEnabled?: boolean;
  /** Stop any in-flight playback of this sound before starting the next. */
  interrupt?: boolean;
  /** Called once the underlying instance is available. */
  onload?: () => void;
  /** Everything else (notably `sprite`) is forwarded to Howl untouched. */
  [key: string]: unknown;
};

// Wrapper-only extra options.
type WrapperOptions = SoundOptions & {
  /**
   * Bypass the table-view gate and play everywhere (the waitlist "Table Ready"
   * alert, which must be audible in the lobby too).
   */
  alwaysPlay?: boolean;
  /**
   * Play even from a table the player is NOT looking at.
   *
   * This is for the cues whose whole job is to pull the player to another
   * table: your turn starting, the 5s countdown, out of time. Ambient table
   * sound must never set this or the bleed returns (EN-10577).
   *
   * Opts out of BOTH places the "is this panel the one on screen" question is
   * asked — the active-table gate, and the inactive half of the arrival snap.
   * Missing the second one is what silenced the countdown completely on the
   * first cut of EN-10577: `useTableSnap` reports a background panel as
   * permanently snapping, so the cue was dropped from every table including
   * the focused one. The lobby route gate, master mute, and the real arrival
   * window (backlog replay on switching tables) all still apply.
   */
  crossTable?: boolean;
  /**
   * Opt OUT of the arrival snap (below): this cue describes the state the player
   * is arriving into rather than an action that already happened, so it should
   * still sound on the first frames of a table switch. Used only by the
   * "it is your turn" alert.
   */
  playOnArrival?: boolean;
  /**
   * Feedback for a tap the player just made, rather than something a table
   * did. Skips both halves of "is the player looking at this table" — a finger
   * on the screen has already answered it, and more strongly.
   *
   * The bet/raise tray is the only cue of this kind today, and it needs the
   * exemption because of where the grid keeps its focus handler: focusing a
   * cell is the CELL's onClick, so the Bet button inside it runs FIRST and the
   * focus change bubbles up afterwards. The tap that opened the tray was
   * therefore judged against the table's state from before it was focused and
   * dropped as `not-active-table`, while the confirm tap seconds later — by
   * which time the table was focused — sounded normally (owner, 8/27).
   *
   * Focusing before playing would not fix that. React state is asynchronous,
   * so the gates could not have updated within that same tap; and committing
   * the switch OPENS an arrival window (useSnapOnArrival re-holds whenever a
   * table becomes active), which would then drop the cue as `snap` instead.
   * A gesture has to clear both gates or it clears neither.
   *
   * Deliberately not past anything else: the lobby route gate, the master
   * mute and the dedupe claim all still apply.
   */
  userGesture?: boolean;
  /**
   * Play this cue at most once per table per window, across every mounted copy
   * of that table (carousel panel AND grid cell).
   *
   * The claim is spent HERE, after every gate has passed and immediately before
   * the sample starts — never by the caller. Claiming in the component looks
   * equivalent and is not: `sound` is null on a component's first render and
   * resolves in an effect, so a component that mounts at the moment of its cue
   * (WinnerMsg does — it mounts when the win lands) claimed on a call that then
   * returned early for `audio-not-loaded`, and the retry once the audio arrived
   * was refused by its own claim. Silent every time, focused table included.
   *
   * Same failure the countdown had for a different reason: a claim handed to
   * something that could not play it.
   */
  dedupeKey?: string;
};

type Exposed = {
  sound: Howl | null;
  stop: (id?: number) => void;
  pause: (id?: number) => void;
  duration: number | null;
};

/**
 * Table SFX hook. Same call signature and return shape as `use-sound`
 * (`[play, { sound, stop, pause, duration }]`), with two deliberate differences:
 *
 * (1) SHARED INSTANCES. The underlying Howl comes from utils/soundCache, so it is
 * constructed once per (src + options) pair for the whole session instead of once
 * per component mount. This is a table-switch performance fix: the per-table
 * subtree remounts on every switch, and ~50 Howl constructions (XHR + decode +
 * Web Audio nodes) used to land in the frames the switch needed. It also removes
 * the leak the old code documented — 483 Howls created and 0 freed across a
 * 4-table replay — because there is now nothing per-mount to leak.
 *
 * Consequence: unmounting no longer unloads (nothing to unload — the instance is
 * shared), so a short SFX that was playing while you switch tables now finishes
 * instead of being cut off mid-sample.
 *
 * (2) SOUND SCOPE. Every play is gated three ways, all opt-out-able:
 *   • {@link areTableSoundsEnabled} — game SFX stay silent in the lobby
 *     (`alwaysPlay: true` opts out; the waitlist alert does).
 *   • the panel must be the table the player is on — a kept-alive or
 *     mid-slide panel, and every non-focused grid cell, never makes noise
 *     (`alwaysPlay` or `crossTable` opts out; the attention cues use
 *     `crossTable` so they are heard from a table you are not looking at).
 *   • the table must not be snapping — on arrival at a table (and during a
 *     reconnect resync) the cues belonging to state that piled up while the
 *     player was away are dropped, the audio half of snap-on-arrival
 *     (`playOnArrival: true` opts out; only the your-turn alert does).
 */
const useSound = (
  src: string | string[],
  options?: WrapperOptions
): [(playOptions?: PlayOptions) => void, Exposed] => {
  const {
    alwaysPlay = false,
    crossTable = false,
    playOnArrival = false,
    userGesture = false,
    dedupeKey,
    volume = 1,
    playbackRate = 1,
    soundEnabled = true,
    interrupt = false,
    onload,
    // `id` is a play-time concern in use-sound's API, never a Howl option.
    id: _ignoredId,
    ...delegated
  } = (options ?? {}) as WrapperOptions;

  // Everything that defines the constructed instance. Two call sites that differ
  // only in volume must NOT share one Howl, so volume/rate are part of the key.
  const howlVars = useMemo(
    () => ({ volume, rate: playbackRate, ...delegated }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [volume, playbackRate, JSON.stringify(delegated)]
  );
  const cacheKey = soundCacheKey(src, howlVars);

  // A mounted-but-inert table panel (outgoing mid-slide, or kept alive by the
  // carousel) must stay silent — otherwise switching tables would play the other
  // table's SFX. `true` wherever no provider exists: app-level sounds and every
  // grid cell keep behaving as before.
  const isActiveTable = useContext(TableActiveContext);
  // SNAP THE SOUND, exactly as the visuals are snapped. Arriving at a table
  // applies the state that piled up while the player was away, and the same
  // effects that apply it fire this table's cues — so a switch used to end with
  // the last thing that happened there being announced: a card dealt, chips
  // pulled to the pot, a player timing out. Those are replays of the past, and
  // the ear reads them as belonging to the table just left. Every cue is dropped
  // while the arrival window is open (and during a reconnect resync); the moment
  // it closes, live actions sound normally again. See hooks/useTableSnap.
  const shouldSnap = useTableSnap();
  // The arrival window WITHOUT the "nobody is looking at this panel" term, for
  // the cross-table cues — see useTableArrivalSnap.
  const isArrivalSnapping = useTableArrivalSnap();
  const traceTableId = useContext(TableIdContext);

  const [sound, setSound] = useState<Howl | null>(null);
  // Latest onload, read at delivery time so a re-render can't strand a stale one.
  const onloadRef = useRef(onload);
  onloadRef.current = onload;

  useEffect(() => {
    setSound(null);
    const cancel = acquireSound(src, howlVars, (howl) => {
      setSound(howl);
      onloadRef.current?.();
    });
    // No unload here — the instance is shared (see utils/soundCache).
    return cancel;
    // cacheKey captures src + every Howl option by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // The gates are read through a ref so `play` keeps a STABLE identity. Callers
  // put it in effect dependency arrays (Bet does), so a changing identity would
  // re-run their effects — and the moment a kept-alive panel became active, that
  // re-run would fire the very cue the snap just suppressed. Only `sound` may
  // change the callback, and that happens once, when the audio resolves.
  const gatesRef = useRef({
    alwaysPlay,
    crossTable,
    playOnArrival,
    userGesture,
    dedupeKey,
    isActiveTable,
    shouldSnap,
    isArrivalSnapping,
    soundEnabled,
    interrupt
  });
  gatesRef.current = {
    alwaysPlay,
    crossTable,
    playOnArrival,
    userGesture,
    dedupeKey,
    isActiveTable,
    shouldSnap,
    isArrivalSnapping,
    soundEnabled,
    interrupt
  };

  const play = useCallback(
    (playOptions?: PlayOptions) => {
      const gates = gatesRef.current;
      // Dev-only: name whichever gate said no, so a cue that never sounds is
      // something you can read rather than something you have to infer. No-op
      // unless the SFX lab is switched on (see utils/sfxTrace).
      const blocked = (why: string | null) =>
        recordSfx({
          src: shortSrc(src),
          blockedBy: why,
          gameID: String(traceTableId ?? ''),
          crossTable: gates.crossTable,
          isActiveTable: gates.isActiveTable
        });

      if (!sound) return blocked('audio-not-loaded');
      if (!gates.alwaysPlay && !areTableSoundsEnabled()) return blocked('route-gate');
      // IS THE PLAYER LOOKING AT THIS TABLE? Both gates below ask a version of
      // that, so both are skipped by the two kinds of cue that answer it some
      // other way: the lobby-wide alert, and a cue the player's own finger
      // started (see `userGesture` — passing one gate and not the other just
      // moves where the tap gets dropped).
      if (!gates.alwaysPlay && !gates.userGesture) {
        if (!gates.crossTable && !gates.isActiveTable) return blocked('not-active-table');
        // SNAP-ON-ARRIVAL. A cross-table cue checks only the arrival window:
        // the ordinary snap ALSO means "nobody is looking at this panel",
        // which is true of every background table forever — applying it here
        // silenced the countdown everywhere, focused table included, because
        // the inactive copy of the table claimed the cue and was then dropped
        // (EN-10577).
        if (!gates.playOnArrival) {
          if (gates.crossTable ? gates.isArrivalSnapping() : gates.shouldSnap())
            return blocked(gates.crossTable ? 'arrival-snap' : 'snap');
        }
      }
      if (!gates.soundEnabled && !playOptions?.forceSoundEnabled) return blocked('soundEnabled');
      // LAST gate, so a claim is only ever spent on a play that happens.
      const dedupe = playOptions?.dedupeKey ?? gates.dedupeKey;
      if (dedupe && !claimCue(String(traceTableId ?? ''), dedupe, Date.now()))
        return blocked('deduped');
      blocked(null);
      if (gates.interrupt) sound.stop();
      if (playOptions?.playbackRate) sound.rate(playOptions.playbackRate);
      sound.play(playOptions?.id);
    },
    [sound]
  );

  const stop = useCallback(
    (playId?: number) => {
      sound?.stop(playId);
    },
    [sound]
  );

  const pause = useCallback(
    (playId?: number) => {
      sound?.pause(playId);
    },
    [sound]
  );

  const duration = useMemo(() => {
    if (!sound) return null;
    try {
      const secs = sound.duration();
      return typeof secs === 'number' ? secs * 1000 : null;
    } catch {
      return null;
    }
  }, [sound]);

  return [play, { sound, stop, pause, duration }];
};

export default useSound;
